import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import { apiRequest, errorMessage } from '@/api/http';
import { useSchoolYears } from '@/composables/useSchoolYears';
import { gradeNumber, groupAtRiskByStudent, JHS_GRADES, SHS_GRADES } from '@/constants/risk';
import { SchoolLevel } from '@/constants/accounts';
import { gradingPeriods } from '@/constants/gradingPeriods';
import { useOpenPdf } from '@/composables/useOpenPdf';

// Admin dashboard data for the selected school year: enrollment counts,
// students at risk (one row per student, with their subjects) and LARDO
// alerts. JHS/SHS admins only get their own level.
export function useAdminDashboard() {
  const store = useStore();

  // The server limits the data to the admin role's level; the level here
  // only decides which sections and reports to show.
  const roleLevel = computed(() => store.getters.activeRoleLevel);
  const schoolYearId = computed(() => store.getters.getFilterSelected);
  const { years } = useSchoolYears();
  const selectedYear = computed(() => years.value.find((y) => y.id === schoolYearId.value));
  const schoolYear = computed(() => selectedYear.value?.school_year || '');

  const data = shallowRef(null);
  const loading = shallowRef(false);
  const error = shallowRef('');

  async function load() {
    if (!schoolYearId.value) return;
    loading.value = true;
    error.value = '';
    try {
      data.value = await apiRequest(
        'get',
        `/enroll-student/getAdminDashboardData/${schoolYearId.value}`,
      );
    } catch (e) {
      error.value = errorMessage(e, 'Could not load the dashboard.');
    } finally {
      loading.value = false;
    }
  }
  watch(schoolYearId, load, { immediate: true });

  const levels = computed(() => ({
    jhs: roleLevel.value !== SchoolLevel.Senior,
    shs: roleLevel.value !== SchoolLevel.Junior,
  }));
  const grades = computed(() => [
    ...(levels.value.jhs ? JHS_GRADES : []),
    ...(levels.value.shs ? SHS_GRADES : []),
  ]);

  const counts = computed(() => {
    const d = data.value || {};
    const total =
      d.totalStudents ??
      (levels.value.jhs ? d.juniorCount || 0 : 0) + (levels.value.shs ? d.seniorCount || 0 : 0);
    return {
      total,
      jhs: d.juniorCount || 0,
      shs: d.seniorCount || 0,
      atRisk: d.riskCount || 0,
      lardo: d.lardoCount || 0,
    };
  });

  // The API sends one row per student and subject; group them by student.
  const atRiskStudents = computed(() => groupAtRiskByStudent(data.value?.atRisk || []));

  const lardoAlerts = computed(() =>
    (data.value?.lardo || []).map((row) => ({
      ...row,
      id: row.id ?? row.studentID,
      name: row.student_name,
      grade: gradeNumber(row.grade_level),
      pending: !row.action_taken,
    })),
  );
  const lardoPending = computed(() => lardoAlerts.value.filter((a) => a.pending).length);

  // Per grade level, for the breakdown cards.
  const byGrade = computed(() =>
    grades.value.map((g) => ({
      grade: g,
      label: `Grade ${g}`,
      enrolled: Number(data.value?.enrolledByGrade?.[`Grade ${g}`] || 0),
      atRisk: atRiskStudents.value.filter((s) => s.grade === g).length,
      lardo: lardoAlerts.value.filter((a) => a.grade === g).length,
    })),
  );

  // PDF reports, opened in a new tab. They're fetched with the login token:
  // the server takes the department from the admin's role and prints the
  // JHS/SHS Admin as "Prepared by".
  const reports = computed(() => {
    const sy = schoolYearId.value;
    const list = [];
    if (levels.value.jhs) {
      list.push({ title: 'JHS student list', path: `/pdf-generator/getAllStudenListByLevel/${sy}/${encodeURIComponent('Junior High')}` });
    }
    if (levels.value.shs) {
      list.push({ title: 'SHS student list', path: `/pdf-generator/getAllStudenListByLevel/${sy}/${encodeURIComponent('Senior High')}` });
    }
    list.push(
      { title: 'Students at risk', path: `/pdf-generator/getAllAtRiskStudents/${sy}` },
      { title: 'LARDO students', path: `/pdf-generator/getAllLardoStudents/${sy}` },
      { title: 'Summary of students', path: `/pdf-generator/studentSummary/${sy}` },
      {
        title: 'At-risk & LARDO summary',
        options: gradingPeriods(selectedYear.value?.syType).map((period) => ({
          title: period,
          path: `/pdf-generator/atRiskLardoSummary/${sy}/${encodeURIComponent(period)}`,
        })),
      },
    );
    return list;
  });

  const openPdf = useOpenPdf();
  async function openReport(report) {
    await openPdf(report.path);
  }

  return {
    schoolYear,
    loading,
    error,
    load,
    levels,
    grades,
    counts,
    atRiskStudents,
    lardoAlerts,
    lardoPending,
    byGrade,
    reports,
    openReport,
  };
}
